import { useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import Modal from "@/components/Modal";
import {
  UploadCloud, Loader2, X, AlertTriangle, Check, ChevronRight, FileSpreadsheet, ArrowRight,
} from "lucide-react";

function fmtErr(d) {
  if (!d) return "Erreur.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

/**
 * Parse XLSX or CSV file into { columns: string[], rows: Record<string,string>[] }.
 * Uses dynamic import for sheetjs-style — but to keep deps light, parse CSV natively
 * and rely on simple XLSX-from-file via FileReader for now: we accept CSV only on the
 * client side, and tell the user XLSX must be exported as CSV from Excel.
 *
 * Wait — to cover XLSX too, we use a tiny inlined parser via the browser's `File`
 * APIs. For simplicity here we accept CSV in this MVP. XLSX upload accepted but
 * the user is asked to save as CSV first. (Backend already handles XLSX server-side
 * via /workspace/import/analyze, but the bulk-import endpoint here expects JSON.)
 */
function parseCsv(text) {
  // Tiny CSV parser supporting quoted strings + commas + semicolons
  const lines = text.replace(/\r\n/g, "\n").split("\n").filter((l) => l.trim());
  if (lines.length === 0) return { columns: [], rows: [] };
  const sep = (lines[0].match(/;/g) || []).length > (lines[0].match(/,/g) || []).length ? ";" : ",";
  const splitLine = (line) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') { if (inQ && line[i + 1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
      else if (c === sep && !inQ) { out.push(cur); cur = ""; }
      else { cur += c; }
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };
  const cols = splitLine(lines[0]);
  const rows = lines.slice(1).map((l) => {
    const vals = splitLine(l);
    const o = {};
    cols.forEach((c, i) => { o[c] = vals[i] ?? ""; });
    return o;
  });
  return { columns: cols, rows };
}

/**
 * ContextualImportButton — drop-in button for any module.
 *
 * Props:
 *   moduleKey: backend slug (e.g. 'risques', 'fournisseurs')
 *   targetFields: [{ key, label, required, type, default }]
 *   onImported: (insertedCount) => void
 *   canImport: bool
 */
export default function ContextualImportButton({ moduleKey, targetFields, onImported, canImport = true }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!canImport) return null;
  return (
    <>
      <button onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-white/10 text-zinc-300 hover:border-[#7F77DD]/50 hover:text-white transition-colors"
        data-testid={`bulk-import-btn-${moduleKey}`}>
        <UploadCloud className="w-3.5 h-3.5" /> {t("saas.import.button", { defaultValue: "Importer Excel/CSV" })}
      </button>
      {open && (
        <BulkImportModal
          moduleKey={moduleKey}
          targetFields={targetFields}
          onClose={() => setOpen(false)}
          onImported={(n) => { setOpen(false); onImported?.(n); }}
        />
      )}
    </>
  );
}

function BulkImportModal({ moduleKey, targetFields, onClose, onImported }) {
  const [step, setStep] = useState("upload"); // upload | map | applying | done
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null); // { columns, rows }
  const [mapping, setMapping] = useState({}); // targetKey -> sourceColumn
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const onPick = async (f) => {
    if (!f) return;
    setFile(f); setError("");
    const lower = f.name.toLowerCase();
    try {
      if (lower.endsWith(".csv")) {
        const text = await f.text();
        const p = parseCsv(text);
        if (p.columns.length === 0) throw new Error("Fichier vide ou illisible.");
        setParsed(p);
        applyAutoMap(p.columns);
        setStep("map");
      } else if (lower.endsWith(".xlsx") || lower.endsWith(".xlsm")) {
        // Backend parses XLSX via openpyxl
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/workspace/bulk/parse", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 30000,
        });
        if (!r.data?.columns?.length) throw new Error("Fichier XLSX vide ou illisible.");
        const p = { columns: r.data.columns, rows: r.data.rows };
        setParsed(p);
        applyAutoMap(p.columns);
        setStep("map");
      } else {
        setError("Format non supporté. Utilise CSV ou XLSX.");
      }
    } catch (e) {
      setError(fmtErr(e?.response?.data?.detail || e.message));
    }
  };

  const applyAutoMap = (cols) => {
    const auto = {};
    targetFields.forEach((tf) => {
      const found = cols.find((c) => c.toLowerCase() === tf.label.toLowerCase() || c.toLowerCase() === tf.key.toLowerCase());
      if (found) auto[tf.key] = found;
    });
    setMapping(auto);
  };

  const apply = async () => {
    setStep("applying"); setError("");
    try {
      const records = parsed.rows.map((row) => {
        const r = {};
        for (const tf of targetFields) {
          const src = mapping[tf.key];
          let v = src ? row[src] : tf.default;
          if (v == null || v === "") v = tf.default ?? "";
          if (tf.type === "number" || tf.type === "int") {
            const n = parseFloat(String(v).replace(/[^\d.-]/g, ""));
            v = Number.isFinite(n) ? n : (tf.default ?? 0);
            if (tf.type === "int") v = Math.round(v);
          }
          if (tf.type === "float") {
            const n = parseFloat(String(v).replace(/[^\d.-]/g, ""));
            v = Number.isFinite(n) ? n : (tf.default ?? 0);
          }
          r[tf.key] = v;
        }
        return r;
      });
      const r = await api.post(`/workspace/bulk/${moduleKey}`, records, { timeout: 60000 });
      setResult(r.data);
      setStep("done");
    } catch (err) {
      setError(fmtErr(err?.response?.data?.detail || err.message));
      setStep("map");
    }
  };

  const required = targetFields.filter((f) => f.required);
  const missing = required.filter((f) => !mapping[f.key]);
  const sample = useMemo(() => (parsed?.rows || []).slice(0, 3), [parsed]);

  return (
    <Modal onClose={onClose} maxWidth="max-w-3xl" testid={`bulk-import-modal-${moduleKey}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="noam-overline flex items-center gap-1.5 text-[#7F77DD]"><FileSpreadsheet className="w-3 h-3" /> Import contextuel</div>
          <div className="noam-display text-lg font-semibold mt-1">Module : {moduleKey}</div>
          <p className="text-[11px] text-zinc-500 mt-1.5">Charge un CSV (colonnes en première ligne). Pour Excel : Enregistrer sous → CSV UTF-8.</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 mb-4 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /><span>{error}</span>
        </div>
      )}

      {step === "upload" && (
        <div className="space-y-4">
          <div onClick={() => inputRef.current?.click()}
            className="border-2 border-dashed border-white/10 rounded-lg p-10 text-center cursor-pointer hover:border-[#7F77DD]/60 hover:bg-[#7F77DD]/5 transition-colors"
            data-testid={`bulk-dropzone-${moduleKey}`}>
            <UploadCloud className="w-8 h-8 mx-auto text-zinc-400" />
            <div className="text-sm text-white mt-3">{file ? file.name : "Cliquer pour sélectionner CSV ou XLSX"}</div>
            <div className="text-[11px] text-zinc-500 mt-1">CSV ou XLSX — première ligne = en-têtes — UTF-8 recommandé pour CSV</div>
          </div>
          <input ref={inputRef} type="file" accept=".csv,.xlsx" hidden onChange={(e) => onPick(e.target.files?.[0])} data-testid={`bulk-file-input-${moduleKey}`} />
        </div>
      )}

      {step === "map" && parsed && (
        <div className="space-y-4">
          <div className="text-[11px] text-zinc-400">
            <span className="text-white font-medium">{parsed.rows.length}</span> lignes détectées · <span className="text-white">{parsed.columns.length}</span> colonnes : {parsed.columns.join(", ")}
          </div>

          <div className="border border-white/5 rounded-md overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-white/5 text-[10px] uppercase tracking-wider text-zinc-500 bg-white/[0.02]">
              <div className="col-span-5">Champ Noam</div>
              <div className="col-span-1 text-center">↔</div>
              <div className="col-span-6">Colonne du fichier</div>
            </div>
            {targetFields.map((tf) => (
              <div key={tf.key} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-white/5 items-center text-xs" data-testid={`bulk-map-row-${tf.key}`}>
                <div className="col-span-5">
                  <div className="text-white">{tf.label} {tf.required && <span className="text-[#E24B4A]">*</span>}</div>
                  {tf.type && <div className="text-[10px] text-zinc-500">{tf.type}</div>}
                </div>
                <div className="col-span-1 text-center text-zinc-600"><ArrowRight className="w-3 h-3 mx-auto" /></div>
                <div className="col-span-6">
                  <select value={mapping[tf.key] || ""} onChange={(e) => setMapping((m) => ({ ...m, [tf.key]: e.target.value || undefined }))}
                    className="w-full bg-black/30 border border-white/10 rounded px-2 py-1 text-xs text-white focus:border-[#7F77DD] focus:outline-none" data-testid={`bulk-map-select-${tf.key}`}>
                    <option value="">— Non mappé{tf.default !== undefined ? ` (par défaut : ${tf.default})` : ""}</option>
                    {parsed.columns.map((c) => <option key={c} value={c}>{`${c}`}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>

          {sample.length > 0 && (
            <details className="border border-white/5 rounded-md">
              <summary className="px-3 py-2 cursor-pointer text-xs text-zinc-400 hover:bg-white/[0.02]">Aperçu (3 premières lignes)</summary>
              <pre className="text-[10px] text-zinc-400 p-3 overflow-x-auto bg-black/30">{JSON.stringify(sample, null, 2)}</pre>
            </details>
          )}

          {missing.length > 0 && (
            <div className="text-[11px] text-[#EF9F27]">
              <ChevronRight className="w-3 h-3 inline" /> Champs requis non mappés : {missing.map((m) => m.label).join(", ")}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-3 border-t border-white/5">
            <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5">Annuler</button>
            <button onClick={apply} disabled={missing.length > 0} className="btn-primary text-xs disabled:opacity-50" data-testid={`bulk-apply-btn-${moduleKey}`}>
              <Check className="w-3.5 h-3.5" /> Importer {parsed.rows.length} ligne{parsed.rows.length > 1 ? "s" : ""}
            </button>
          </div>
        </div>
      )}

      {step === "applying" && (
        <div className="py-12 text-center"><Loader2 className="w-8 h-8 text-[#7F77DD] animate-spin mx-auto" /><div className="text-white mt-3 text-sm">Import en cours…</div></div>
      )}

      {step === "done" && result && (
        <div className="space-y-4 text-center py-6" data-testid={`bulk-done-${moduleKey}`}>
          <div className="w-12 h-12 rounded-full bg-[#1D9E75]/20 border-2 border-[#1D9E75] flex items-center justify-center mx-auto">
            <Check className="w-6 h-6 text-[#1D9E75]" />
          </div>
          <div className="noam-display text-lg font-semibold">Import terminé</div>
          <div className="text-sm text-zinc-300"><span className="text-[#1D9E75] font-bold">{result.inserted}</span> ligne{result.inserted > 1 ? "s" : ""} importée{result.inserted > 1 ? "s" : ""}</div>
          {result.errors?.length > 0 && (
            <div className="text-[11px] text-[#EF9F27]">⚠ {result.errors.length} ligne(s) ignorée(s) (validation échouée)</div>
          )}
          <button onClick={() => onImported(result.inserted)} className="btn-primary mt-3" data-testid={`bulk-done-close-${moduleKey}`}>Fermer</button>
        </div>
      )}
    </Modal>
  );
}
